<script setup>
/*
* @Name 多选面板组件
* @Author xnhotaru
* @Param open 面板打开状态
* @Param list 展示的元素
* @Param checkedList 已选择的元素
* @Param hasAll 是否有全选按钮
* @Param checkedLimit 可选内容个数（配合全选使用，全选为false时生效）
* @Param title 标题
* @Param maxWidth 面板最大宽度
* @Param itemPre 选项（商品）名称前缀
* @Return cancel 点击面板取消按钮事件
* @Return confirm 点击面板确定按钮事件
* tips: 建议在使用面板时使用v-if="open"保持组件渲染在获取数据之后，否则会导致面板缓存数据获取失败。
* */
import {ref, onBeforeMount, onUpdated} from "vue"
import YTFromDialog from "@/components/YTFormDialog/index.vue";
const props = defineProps({
  // 面板打开状态
  open: {
    required: true,
    type: Boolean,
    default: false
  },
  // 展示的元素
  list: {
    required: true,
    type: Array,
    default: []
  },
  // 已选择的元素
  checkedList: {
    type: Array,
    default: []
  },
  // 是否有全选按钮
  hasAll: {
    type: Boolean,
    default: true
  },
  // 可选内容个数（配合全选使用，全选为false时生效）
  checkedLimit: {
    type: Number,
    default: 5
  },
  // 标题
  title: {
    type: String,
    default: "温馨提示"
  },
  // 面板最大宽度
  maxWidth: {
    type: Number,
    default: 400
  },
  // 选项前缀
  itemPre: {
    type: Boolean,
    default: false
  },
  // label名
  labelKey: {
    type: String,
    default: 'name'
  },
  // key名
  valueKey: {
    type: String,
    default: 'value'
  }
})

// 建议v-if渲染时使用open控制组件开关，否则会导致关闭面板后丢失cacheProp和cacheSelect数据
onBeforeMount(() => {
  cacheProp.value = props.list
  cacheSelect.value = props.checkedList
})

// 更新传入的list信息，兼容外部的翻页插槽
onUpdated(() => {
  cacheProp.value = props.list
})

const emit = defineEmits(['cancel', 'confirm', 'select'])
// 全选状态
const checkAll = ref(props.list.length === props.checkedList.length)
// 缓存属性选项
const cacheProp = ref([])
// 缓存商品索引值
const cacheIndex = ref()
// 缓存对话框选择属性项
const cacheSelect = ref()
// 处理多选改变
const handleCheckAll = (e) => {
  const checkedCount = cacheProp.value.length
  console.log("cacheSelect", cacheSelect.value)
  emit('select', cacheSelect.value)
  checkAll.value = checkedCount === cacheSelect.value.length
}
// 处理全选
const handleCheckAllProp = () => {
  let all = []
  cacheProp.value.forEach(item => {
    all.push(item[props.valueKey])
  })
  cacheSelect.value = checkAll.value ? all : []
}
// 取消/关闭选择对话框
const handleCancelProp = () => {
  cacheProp.value = null
  cacheIndex.value = null
  cacheSelect.value = []
  checkAll.value = false
  emit('cancel')
}
// 确认选择
const handleConfirmProp = () => {
  emit('confirm', cacheSelect.value)
  handleCancelProp()
}
// 清空已选项
const handleClearCacheSelect = () => {
  cacheSelect.value = []
}
defineExpose({
  handleClearCacheSelect
})
</script>

<template>
  <YTFromDialog v-if="props.open" :title="props.title" :open="props.open" :max-width="props.maxWidth">
    <template v-slot:content>
      <slot name="top"></slot>
      <div v-if="props.list.length > 0">
        <el-checkbox-group v-model="cacheSelect">
          <div v-for="(item, index) in cacheProp">
            <el-checkbox
                @change="handleCheckAll"
                :key="`checkedPanel${item[labelKey]}${index}`"
                :label="`${itemPre ? item.pre + '-' + item[labelKey] : item[labelKey]}`"
                :value="item[valueKey]"
                :disabled="!props.hasAll && cacheSelect.length >= props.checkedLimit && !cacheSelect.some(res => res === item[valueKey])"
            />
          </div>
        </el-checkbox-group>
      </div>
      <div v-else>
        <div class="tips_default txt_center">
          <v-icon icon="mdi-archive-alert-outline" style="font-size: 48px"></v-icon>
          <div>暂无可选项</div>
        </div>
      </div>
      <slot name="pagination"></slot>
      <v-divider style="margin: 20px 0;"></v-divider>
    </template>
    <template v-slot:actions>
      <div style="width: 100%" :class="props.hasAll ? 'flex_bt' : 'flex_right'">
        <el-checkbox
            v-if="props.hasAll"
            v-model="checkAll"
            @change="handleCheckAllProp"
        >
          全选
        </el-checkbox>
        <div>
          <v-btn variant="elevated" class="mg_r_1" @click="handleCancelProp">取消</v-btn>
          <v-btn variant="elevated" color="primary" @click="handleConfirmProp">确定</v-btn>
        </div>
      </div>
    </template>
  </YTFromDialog>
</template>

<style scoped>

</style>
